import React, { useState, useCallback } from "react";
import { Youtube, X, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { extractYouTubeId } from "@/components/LiveYouTubePlayer";

/**
 * LiveYouTubeLauncher — Bigo-style co-watch launcher.
 *
 * Renders a compact YouTube toolbar button. Tapping it opens a
 * cyberpunk-styled modal ("Paste YouTube link") with an input + Load
 * button. On submit we POST to `/live/{room}/youtube` which broadcasts
 * the `play` event to every participant via LiveKit's data channel.
 *
 * When the room already has an active co-watch, tapping the button
 * offers to stop the current video first, matching Bigo's "swap video"
 * flow.
 */
export default function LiveYouTubeLauncher({ roomName, active, dataTestId }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  const closeModal = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setLink("");
  }, [busy]);

  const load = useCallback(async () => {
    const vid = extractYouTubeId(link);
    if (!vid) {
      toast.error("That doesn't look like a YouTube link");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/live/${roomName}/youtube`, {
        action: "play",
        video_id: vid,
        position_seconds: 0,
      });
      toast.success("Video loaded — everyone in the room can watch");
      setOpen(false);
      setLink("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load video");
    } finally {
      setBusy(false);
    }
  }, [link, roomName]);

  const stop = useCallback(async () => {
    setBusy(true);
    try {
      await api.post(`/live/${roomName}/youtube`, { action: "stop" });
      toast.success("Co-watch ended");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't end co-watch");
    } finally {
      setBusy(false);
    }
  }, [roomName]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={dataTestId || "live-youtube-launcher"}
        aria-label={active ? "Change co-watch video" : "Co-watch a YouTube video"}
        title={active ? "Change or stop the co-watch video" : "Co-watch a YouTube video"}
        className={`p-2 transition-colors flex items-center gap-1 ${
          active
            ? "bg-[#FF0000] text-white shadow-[0_0_16px_rgba(255,0,0,0.55)]"
            : "bg-white/10 hover:bg-white/20 text-white/80"
        }`}
      >
        <Youtube className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Load a YouTube video"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0A0A0C] border-2 border-[#FF007F] shadow-[0_0_60px_rgba(255,0,127,0.4)]"
            data-testid="live-youtube-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-11 bg-gradient-to-r from-[#FF0000]/25 via-[#FF007F]/20 to-transparent border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="bg-[#FF0000] text-white text-[10px] font-black px-1.5 py-0.5 flex items-center gap-1">
                  <Youtube className="w-3 h-3" />
                  YOUTUBE
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-white/70">
                  co-watch
                </span>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={busy}
                className="p-1.5 bg-white/5 hover:bg-white/20 text-white/80 disabled:opacity-40"
                aria-label="Close"
                data-testid="live-youtube-modal-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs font-bold text-white/90 uppercase tracking-widest">
                  Paste a YouTube link
                </p>
                <p className="text-[11px] font-mono text-white/50 mt-1">
                  everyone in the room watches together, in sync
                </p>
              </div>

              <div className="flex items-center gap-2 bg-black/60 border border-white/10 focus-within:border-[#39FF14] px-3 py-2">
                <Link2 className="w-4 h-4 text-white/40 shrink-0" />
                <input
                  type="url"
                  inputMode="url"
                  autoFocus
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                  placeholder="https://youtube.com/watch?v=…"
                  disabled={busy}
                  data-testid="live-youtube-modal-input"
                  className="flex-1 min-w-0 bg-transparent text-white text-sm outline-none font-mono placeholder:text-white/30 disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2">
                {active && (
                  <button
                    type="button"
                    onClick={stop}
                    disabled={busy}
                    data-testid="live-youtube-modal-stop"
                    className="flex-1 sm:flex-initial px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                  >
                    Stop current
                  </button>
                )}
                <button
                  type="button"
                  onClick={load}
                  disabled={busy || !link.trim()}
                  data-testid="live-youtube-modal-load"
                  className="flex-1 px-4 py-2 bg-[#39FF14] hover:bg-[#5EFF3D] text-black text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(57,255,20,0.4)]"
                >
                  {busy ? "loading…" : active ? "Swap video" : "Start co-watch"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
